import { Component, OnInit } from '@angular/core';
import { LayoutService } from '@common/services/layout.service';
import { ModalService } from '@common/services/modal.service';
import { CalendarComponent } from '@common/UI/calendar/calendar.component';
import { SortableExtractor } from '@common/UI/sortable/sortable.component';
import { Filterable } from '@common/utilities/Filterable';
import { Lookup } from '@common/utilities/Lookup';
import { commit, main } from '@core/operators';
import { ConfigsService } from '@core/services/configs.service';
import { DataService, InvoiceDetailRecord, InvoiceRecord, InvoiceState, invoiceState, OrderRecord, UserRecord, ProductUnit } from '@core/services/data.service';
import { PartnersService } from '@core/services/partners.service';
import { SynchronizationService } from '@core/services/synchronization.service';
import { UsersService } from '@core/services/users.service';
import { Multilingual } from '@core/utilities/multilingual';
import { MessageBoxService } from '@routes/common/message-box.service';
import { TagEditorComponent } from '@routes/editors/tag-editor/tag-editor.component';
import { ViewerComponent } from '@routes/invoices/viewer/viewer.component';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

const lu = {
  orders: new Lookup<OrderRecord>(i => i.ID)
}

export interface Group {
  OrderID: number;
  OrderTag: string;
  PartnerName: string;
  SKU: string;
  Color: string;
  Unit: ProductUnit;
  Reduced: number;
  QuotePrice: number;
  allocated: boolean;
  records: InvoiceDetailRecord[];
}

@Component({
  templateUrl: './verification.component.html',
  styleUrls: ['./verification.component.scss']
})
export class VerificationComponent implements OnInit {

  constructor(
    private layout: LayoutService,
    private ds: DataService,
    private mb: MessageBoxService,
    private sync: SynchronizationService,
    private users: UsersService,
    private partners: PartnersService,
    private modal: ModalService,
    public configs: ConfigsService
  ) { }

  public get loading(): boolean {
    return !this.sync.ready(UsersService, PartnersService, VerificationComponent);
  }

  public invoices = new Filterable<InvoiceRecord>({
    extractor: i => [i.Tag]    
  });

  public details = new Filterable<InvoiceDetailRecord>({
    extractor: i => [i.ActualQuantity],
    grouper: i => [
      { name: Multilingual.getText('Khách hàng'), option: this.partner(i) },
      { name: Multilingual.getText('Loại hàng'), option: Multilingual.getText(i.Unit == 1 ? 'Hàng mét' : 'Hàng cân') }
    ]
  });

  public selected: { invoice: InvoiceRecord, details: Group[][] } = { invoice: null, details: [] };

  private edited: boolean = false;

  private synchronize() {

    this.selected.details = [];
    this.selected.invoice = null;

    let orders: OrderRecord[] = [];
    let invoices: InvoiceRecord[] = [];

    this.sync.with(VerificationComponent).repeat(
      () => this.ds.invoices('imports').pipe(map(data => data ? commit<boolean>(() => invoices = data).return(true) : false)),
      () => this.ds.orders('incompleted').pipe(map(data => data ? commit<boolean>(() => orders = data).return(true) : false))
    );

    this.sync.when(UsersService, PartnersService, VerificationComponent).commit(() => {

      lu.orders.source = orders;
      this.invoices.source = invoices.filter(i => i.PartnerID == 1);

      if (this.invoices.empty) this.mb.informs("Hiện tại không có đơn hàng nhập xưởng nào cần xử lý. Xin vui lòng quay lại sau!");
    });
  }

  ngOnInit(): void {

    main(() => {
      this.layout.media('footer', 'none');
      this.layout.title = Multilingual.getText("Nhận hàng về từ xưởng");
    });

    this.synchronize();
  }

  public get editable(): boolean {
    return this.selected.invoice != null && this.selected.invoice.State == InvoiceState.Editable;
  }

  public get confirmable(): boolean {
    return this.selected.invoice != null && this.selected.invoice.State == InvoiceState.Editable && this.selected.invoice.Details.length > 0 && this.details.source.length < 1 && this.edited;
  }

  public get empty(): boolean {
    return this.selected.details.count(i => i.length > 1) < 1 && this.selected.details.count(i => i.length > 0) < 2;
  }

  public user(id: number): UserRecord {
    return this.users.get(id);
  }

  public partner(detail: InvoiceDetailRecord, def: string = 'TỰ CHIA'): string {
    let order = lu.orders.get(detail.ImportOrderID);
    if (!order) return def;

    let partner = this.partners.get(order.PartnerID);
    return partner ? partner.Name : def;
  }

  public statistics(field: string, groups: Group[], unit?: ProductUnit): number {
    switch (field) {
      case "SKU": return groups.filter(i => !unit || unit == i.Unit).group(i => i.SKU).length;
      case "Color": return groups.filter(i => !unit || unit == i.Unit).group(i => i.SKU + "/" + i.Color).length;
      case "Count": return groups.filter(i => !unit || unit == i.Unit).sum(i => i.records.length);
      case "ActualQuantity": return groups.filter(i => !unit || unit == i.Unit).sum(i => i.records.sum(x => x.ActualQuantity));
      case "ReducedQuantity": return groups.filter(i => !unit || unit == i.Unit).sum(i => i.Reduced);
      case "QuoteQuantity": return groups.filter(i => !unit || unit == i.Unit).sum(i => i.records.sum(x => x.ActualQuantity) - i.Reduced);
      case "Total": return groups.filter(i => !unit || unit == i.Unit).sum(i => (i.records.sum(x => x.ActualQuantity) - i.Reduced) * i.QuotePrice);
      default: return 0;
    }

  }

  public state = invoiceState;

  public sum(field: string, unit?: ProductUnit): number {
    return this.statistics(field, this.selected.details.many(i => i), unit);
  }

  public extractor(field: string): SortableExtractor<InvoiceRecord> {
    return {
      extract: i => {
        switch (field) {
          case 'Count': return i.LengthCount + i.WeightCount;
          default: return i[field];
        }
      }
    }
  }

  public open(invoice: InvoiceRecord) {


    if (invoice.ID > 0 && invoice.Details.length == 0) {
      this.sync.with(VerificationComponent).repeat(() => this.ds.invoices('get', { ID: invoice.ID }).pipe(map(invoices => {
        if (invoices && invoices.length == 1) {
          invoice.Details = invoices[0].Details;
          return true;
        }
        return false;
      })));
    }

    if (invoice.State == InvoiceState.Confirmed) {
      this.sync.when(VerificationComponent, PartnersService).commit(() => {
        let modal = this.modal.show(ViewerComponent, { state: { invoice: invoice } });
        modal.content.unconfirmed.subscribe(result => invoice.State == result.State);
      });

      return;
    }

    this.sync.when(VerificationComponent).commit(() => {

      this.selected.invoice = { ...invoice, Details: [], Allocations: invoice.Allocations.order(i => lu.orders.get(i.OrderID).PartnerID).map(i => ({ ...i })) };
      this.details.source = invoice.Details.map(i => ({ ...i })).group(i => [i.SKU, i.Color].join('/')).order(i => i.key).many(i => i.items.order(x => x.ActualQuantity));
      this.selected.details = [];

      this.edited = false;

      this.editStamp();
    });    
  }

  public close() {

    if (this.edited) {

      this.mb.confirms('discourage', "Đơn nhập hàng có các thay đổi chưa lưu lại. Đóng đơn sẽ không lưu lại các thay đổi đó. Bạn có muốn đóng đơn?",
        () => {

          this.selected.invoice = null;
          this.selected.details = [];
          this.details.source = [];
          this.edited = false;

          return new Observable<boolean>(subscriber => subscriber.next(true));          
        });
    }
    else {

      this.selected.invoice = null;
      this.selected.details = [];
      this.details.source = [];
      this.edited = false;
    }
  }

  public cancel(record: InvoiceDetailRecord) {

    let allocated = record.ImportOrderID != 0;

    if (!allocated) {
      let allocation = this.selected.invoice.Allocations.first(i => i.SKU == record.SKU && i.Color == record.Color && i.QuoteUnitPrice == i.QuoteUnitPrice);

      if (allocation) {
        record.ImportOrderID = allocation.OrderID;

        allocation.Quantity--;
        if (allocation.Quantity == 0) this.selected.invoice.Allocations.remove(allocation);
      }      
    }

    record.ExportOrderID = 0;
    record.CustomerID = 1;

    let id = 0;

    let order: Group[] = this.selected.details.first(i => i[0].OrderID == id, []);

    let detail: Group = order.first(i => i.OrderID == id && i.SKU == record.SKU && i.Color == record.Color && i.QuotePrice == record.QuoteUnitPrice, {
      OrderID: id,
      OrderTag: Multilingual.getText("MẤT HÀNG"),
      PartnerName: '',
      SKU: record.SKU,
      Color: record.Color,
      Unit: record.Unit,
      QuotePrice: record.QuoteUnitPrice,
      Reduced: 0,
      allocated: allocated,
      records: []
    });

    detail.records.push(record);
    detail.Reduced += record.ActualQuantity - record.QuoteQuantity;
    this.selected.invoice.Details.push(record);

    if (!order.contains(detail)) order.push(detail);
    if (!this.selected.details.contains(order)) this.selected.details.push(order);

    let index = this.details.pageIndex;
    this.details.source = this.details.source.filter(i => i.ID != record.ID);
    this.details.pageIndex = index;
    if (this.details.matches.length < 1) this.details.groups.commit(i => i.selected = true);

    this.edited = true;
  }

  public addAll() {
    this.details.matches.commit(i => this.add(i));
  }

  public add(record: InvoiceDetailRecord) {

    let allocated = record.ImportOrderID != 0;

    if (!allocated) {

      let allocation = this.selected.invoice.Allocations.first(i => i.SKU == record.SKU && i.Color == record.Color && i.QuoteUnitPrice == i.QuoteUnitPrice);

      if (allocation) {
        record.ImportOrderID = allocation.OrderID;
        record.CustomerID = allocation.CustomerID;

        allocation.Quantity--;
        if (allocation.Quantity == 0) this.selected.invoice.Allocations.remove(allocation);
      }
      else {
        record.ImportOrderID = 0;
        record.CustomerID = 3;
      }
    }

    record.ExportOrderID = record.ImportOrderID;

    let id = allocated ? record.ImportOrderID : -(record.CustomerID);

    let order: Group[] = this.selected.details.first(i => i[0].OrderID == id, []);

    let detail: Group = order.first(i => i.OrderID == id && i.SKU == record.SKU && i.Color == record.Color && i.QuotePrice == record.QuoteUnitPrice, {
      OrderID: id,
      OrderTag: allocated ? record.ImportOrderTag : Multilingual.getText('TỰ CHIA'),
      PartnerName: this.partner(record, 'KHO'),
      SKU: record.SKU,
      Color: record.Color,
      Unit: record.Unit,
      QuotePrice: record.QuoteUnitPrice,
      Reduced: 0,
      allocated: allocated,
      records: []
    });

    detail.records.push(record);
    detail.Reduced += record.ActualQuantity - record.QuoteQuantity;
    this.selected.invoice.Details.push(record);

    if (!order.contains(detail)) order.push(detail);
    if (!this.selected.details.contains(order)) this.selected.details.push(order);


    let index = this.details.pageIndex;
    this.details.source = this.details.source.filter(i => i.ID != record.ID);
    this.details.pageIndex = index;
    if (this.details.matches.length < 1) this.details.groups.commit(i => i.selected = true);
    
    this.edited = true;
  }

  public remove(record: InvoiceDetailRecord) {

    let detail: Group = this.selected.details.many(i => i).first(i => i.records.contains(record));
    let order: Group[] = this.selected.details.first(i => i.contains(detail));

    detail.records.remove(record);
    detail.Reduced -= record.ActualQuantity - record.QuoteQuantity;

    if (detail.records.length < 1) {
      order.remove(detail);
      if (order.length < 1) {
        this.selected.details.remove(order);
      }
    }

    if (!detail.allocated) {

      let allocation = record.ImportOrderID > 0
        ? this.selected.invoice.Allocations.first(i => i.OrderID == record.ImportOrderID && i.SKU == record.SKU && i.Color == record.Color && i.QuoteUnitPrice == record.QuoteUnitPrice, {
          OrderID: record.ImportOrderID,
          CustomerID: record.CustomerID,
          SKU: record.SKU,
          Color: record.Color,
          Quantity: 0,
          QuoteUnitPrice: record.QuoteUnitPrice,
        })
        : null;

      if (allocation) {
        allocation.Quantity++;
        if (allocation.Quantity < 2) this.selected.invoice.Allocations = this.selected.invoice.Allocations.union([allocation]).order(i => lu.orders.get(i.OrderID).PartnerID).map(i => ({ ...i }));
      }

      record.ImportOrderID = 0;
      record.CustomerID = 0;
    }    

    record.ExportOrderID = 0;      

    this.selected.invoice.Details.remove(record);

    this.details.source = this.details.source.union([record]).group(i => [i.SKU, i.Color].join('/')).many(i => i.items.sort((a, b) => a.ActualQuantity > b.ActualQuantity ? 1 : -1));

    this.edited = true;
  }

  public editStamp() {

    if (!this.editable) return;

    let ref = this.modal.show(CalendarComponent, { state: { selected: new Date(this.selected.invoice.Stamp) } });

    ref.content.change.subscribe(value => {
      this.selected.invoice.Stamp = value.getTime();
      ref.hide();
      this.edited = true;
    });
  }

  public editTag() {

    let ref = this.modal.show(TagEditorComponent, { state: { tag: { InvoiceID: this.selected.invoice.ID, Tag: this.selected.invoice.Tag, PartnerID: this.selected.invoice.PartnerID, Stamp: this.selected.invoice.Stamp } } });

    ref.content.change.subscribe(value => {
      this.selected.invoice.Tag = value.Tag;
      ref.hide();
    });
  }

  public confirm() {

    this.mb.confirms("encourage", "Bạn muốn xác nhận đơn đặt hàng đang mở?",

      () => {

        this.sync.notify(VerificationComponent, false);

        return this.ds.invoices('confirm', this.selected.invoice).pipe(map(invoices => {

          this.sync.notify(VerificationComponent, true);

          if (invoices && invoices.length == 1) {
            this.invoices.source = this.invoices.source.filter(i => i.ID != invoices[0].ID).union(invoices);
            this.edited = false;
            this.close();
            return true;
          }

          return false;
        }))
      });
  }
}
